import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Rating,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DOMPurify from 'dompurify';
import { useCustomToast, ToastVariant } from '../../components/Methods/SnackBar';
import axios from 'axios';
import { apiEndpoint } from '../../Service/Apis';
import Loading from '../../components/Loading/Loading';
import PageTitle from '../../components/Page Component/PageTitle';
import { AUTH_HEADER, richHtmlSx, STATUS_COLORS } from '../../utils/trainingConstants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TrainingAssignment {
  id: string;
  employee_name: string;
  training_name: string;
  copied_sheet_url: string;
  start_date: string;
  end_date: string;
  status: string;
  is_overdue: boolean;
  review_manager_name: string;
  cumulative_manager_score: number | null;
  has_feedback: boolean;
  comment: string;
  hr_comment: string | null;
}

interface FeedbackDetail {
  id: string;
  comment: string;
  cumulative_score: number | null;
  responses: Array<{
    id: string;
    question: string;
    question_text: string;
    question_type: string;
    question_order: number;
    rating?: number;
    comment?: string;
  }>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MyTrainings: React.FC = () => {
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  // Dialog states
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<TrainingAssignment | null>(null);
  const [openScoreDialog, setOpenScoreDialog] = useState(false);
  const [viewingFeedback, setViewingFeedback] = useState<FeedbackDetail | null>(null);
  const [commentDialog, setCommentDialog] = useState({ open: false, content: '', title: 'Comment' });

  const showToast = useCustomToast();

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  useEffect(() => {
    fetchMyTrainings();
  }, []);

  const fetchMyTrainings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiEndpoint}/skillset/training/assignments/`, {
        headers: AUTH_HEADER(),
      });
      setAssignments(response.data);
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to fetch trainings', ToastVariant.ERROR, true);
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Status updates
  // -------------------------------------------------------------------------

  const handleStatusUpdate = useCallback(
    async (assignmentId: string, newStatus: string) => {
      const confirmMessages: Record<string, string> = {
        in_progress: 'Are you sure you want to start this training?',
        under_evaluation: 'Have you completed the training? Submit it for manager evaluation?',
      };

      if (confirmMessages[newStatus] && !window.confirm(confirmMessages[newStatus])) return;

      setUpdatingStatus(assignmentId);
      try {
        await axios.patch(
          `${apiEndpoint}/skillset/training/assignments/${assignmentId}/status/`,
          { status: newStatus },
          { headers: AUTH_HEADER() }
        );

        const successMessages: Record<string, string> = {
          in_progress: 'Training started successfully',
          under_evaluation: 'Training submitted for evaluation',
        };

        showToast(successMessages[newStatus] || 'Status updated successfully', ToastVariant.SUCCESS, true);
        fetchMyTrainings();
      } catch (error: any) {
        showToast(error.response?.data?.detail || 'Failed to update status', ToastVariant.ERROR, true);
      } finally {
        setUpdatingStatus(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showToast]
  );

  // -------------------------------------------------------------------------
  // Score popup
  // -------------------------------------------------------------------------

  const handleViewScore = async (assignmentId: string) => {
    try {
      const response = await axios.get(
        `${apiEndpoint}/skillset/training/assignments/${assignmentId}/feedback/`,
        { headers: AUTH_HEADER() }
      );
      setViewingFeedback(response.data);
      setOpenScoreDialog(true);
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to fetch feedback details', ToastVariant.ERROR, true);
    }
  };

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  const openCommentPopup = (content: string, title: string) => {
    setCommentDialog({ open: true, content, title });
  };

  const getActionButton = (assignment: TrainingAssignment) => {
    const isUpdating = updatingStatus === assignment.id;

    if (assignment.status === 'assigned' || assignment.status === 'accepted') {
      return (
        <Tooltip title={isUpdating ? '' : 'Start Training'}>
          <span>
            <Button
              variant="contained"
              size="small"
              startIcon={isUpdating ? undefined : <PlayArrowIcon />}
              disabled={isUpdating}
              onClick={() => handleStatusUpdate(assignment.id, 'in_progress')}
            >
              {isUpdating ? 'Updating...' : 'Start'}
            </Button>
          </span>
        </Tooltip>
      );
    }

    if (assignment.status === 'in_progress') {
      return (
        <Tooltip title={isUpdating ? '' : 'Submit for Evaluation'}>
          <span>
            <Button
              variant="contained"
              size="small"
              color="warning"
              startIcon={isUpdating ? undefined : <SendIcon />}
              disabled={isUpdating}
              onClick={() => handleStatusUpdate(assignment.id, 'under_evaluation')}
            >
              {isUpdating ? 'Updating...' : 'Submit'}
            </Button>
          </span>
        </Tooltip>
      );
    }

    if (assignment.status === 'evaluated' || assignment.status === 'completed') {
      return (
        <Chip
          label={assignment.status === 'completed' ? 'Completed' : 'Evaluated'}
          color={assignment.status === 'completed' ? 'success' : 'secondary'}
          size="small"
        />
      );
    }

    return (
      <Chip
        label={assignment.status.replace(/_/g, ' ')}
        color={STATUS_COLORS[assignment.status]}
        size="small"
        sx={{ textTransform: 'capitalize' }}
      />
    );
  };

  if (loading) return <Loading />;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
      <Box sx={{ mb: 3 }}>
        <PageTitle>My Trainings</PageTitle>
        <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
          View and manage your assigned trainings
        </Typography>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No.</TableCell>
                <TableCell>Training Name</TableCell>
                <TableCell align="center">Training Sheet</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Review Manager</TableCell>
                <TableCell align="center">Assignment Comment</TableCell>
                <TableCell align="center">HR Comment</TableCell>
                <TableCell align="center">Manager Score</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                      No trainings assigned yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                assignments.map((assignment, index) => (
                  <TableRow key={assignment.id} hover>
                    <TableCell>{index + 1}</TableCell>

                    {/* Training name — click to open detail dialog */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                        onClick={() => {
                          setSelectedAssignment(assignment);
                          setOpenDetailsDialog(true);
                        }}
                      >
                        {assignment.training_name}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      {assignment.copied_sheet_url && (
                        <Tooltip title="Open Training Sheet">
                          <IconButton size="small" color="primary" onClick={() => window.open(assignment.copied_sheet_url, '_blank')}>
                            <LinkIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>

                    <TableCell>{new Date(assignment.start_date).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(assignment.end_date).toLocaleDateString()}</TableCell>

                    {/* Status — show overdue badge when applicable */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip
                          label={assignment.status.replace(/_/g, ' ')}
                          color={STATUS_COLORS[assignment.status]}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                        {assignment.is_overdue && (
                          <Chip label="Overdue" color="error" size="small" />
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>{assignment.review_manager_name}</TableCell>

                    {/* Assignment comment */}
                    <TableCell align="center">
                      {assignment.comment ? (
                        <Tooltip title="View Assignment Comment">
                          <IconButton size="small" onClick={() => openCommentPopup(assignment.comment, 'Assignment Comment')}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="textSecondary">—</Typography>
                      )}
                    </TableCell>

                    {/* HR comment */}
                    <TableCell align="center">
                      {assignment.hr_comment ? (
                        <Tooltip title="View HR Comment">
                          <IconButton size="small" onClick={() => openCommentPopup(assignment.hr_comment!, 'HR Comment')}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="textSecondary">—</Typography>
                      )}
                    </TableCell>

                    {/* Manager score — click to view full feedback */}
                    <TableCell align="center">
                      {assignment.cumulative_manager_score !== null ? (
                        <Tooltip title="Click to view feedback details">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                            onClick={() => handleViewScore(assignment.id)}
                          >
                            {assignment.cumulative_manager_score.toFixed(1)} / 5
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          {assignment.status === 'under_evaluation' ? 'Pending' : 'N/A'}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">{getActionButton(assignment)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ------------------------------------------------------------------ */}
      {/* Comment viewer (reused for assignment & HR comments)               */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={commentDialog.open} onClose={() => setCommentDialog({ open: false, content: '', title: '' })} maxWidth="md" fullWidth>
        <DialogTitle>{commentDialog.title}</DialogTitle>
        <DialogContent>
          <Box
            sx={{ ...richHtmlSx, mt: 1, maxHeight: '65vh' }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(commentDialog.content) }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialog({ open: false, content: '', title: '' })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Training Details Dialog                                            */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Training Details</DialogTitle>
        <DialogContent>
          {selectedAssignment && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Training Name</Typography>
                <Typography variant="body1">{selectedAssignment.training_name}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={selectedAssignment.status.replace(/_/g, ' ')}
                    color={STATUS_COLORS[selectedAssignment.status]}
                    size="small"
                    sx={{ textTransform: 'capitalize', mt: 0.5 }}
                  />
                  {selectedAssignment.is_overdue && <Chip label="Overdue" color="error" size="small" sx={{ mt: 0.5 }} />}
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Duration</Typography>
                <Typography variant="body1">
                  {new Date(selectedAssignment.start_date).toLocaleDateString()} –{' '}
                  {new Date(selectedAssignment.end_date).toLocaleDateString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Review Manager</Typography>
                <Typography variant="body1">{selectedAssignment.review_manager_name}</Typography>
              </Box>
              {selectedAssignment.cumulative_manager_score !== null && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Manager Score</Typography>
                  <Typography variant="h6" color="primary.main">
                    {selectedAssignment.cumulative_manager_score.toFixed(1)} / 5.0
                  </Typography>
                </Box>
              )}
              {selectedAssignment.comment && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>Assignment Comment</Typography>
                  <Box
                    sx={{ ...richHtmlSx, maxHeight: '250px' }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedAssignment.comment) }}
                  />
                </Box>
              )}
              {selectedAssignment.hr_comment && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>HR Comment</Typography>
                  <Box
                    sx={{ ...richHtmlSx, maxHeight: '250px' }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedAssignment.hr_comment!) }}
                  />
                </Box>
              )}
              {selectedAssignment.copied_sheet_url && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<LinkIcon />}
                    onClick={() => window.open(selectedAssignment.copied_sheet_url, '_blank')}
                  >
                    Open Training Sheet
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Score / Feedback Detail Dialog                                     */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={openScoreDialog} onClose={() => setOpenScoreDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Feedback Score Details</DialogTitle>
        <DialogContent>
          {viewingFeedback && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              {[...viewingFeedback.responses]
                .sort((a, b) => (a.question_order || 0) - (b.question_order || 0))
                .map((response, index) => (
                  <Box key={response.id}>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                      {index + 1}. {response.question_text || response.question}
                    </Typography>
                    {response.question_type === 'stars' || response.rating ? (
                      <Rating value={response.rating || 0} readOnly size="large" />
                    ) : (
                        <Box
                          sx={{ ...richHtmlSx, maxHeight: '200px' }}
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(response.comment || 'No response provided') }}
                        />
                    )}
                  </Box>
                ))}

              {viewingFeedback.cumulative_score != null && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">Average Score (Star Ratings)</Typography>
                  <Typography variant="h5" color="primary">
                    {viewingFeedback.cumulative_score.toFixed(2)} / 5.0
                  </Typography>
                </Box>
              )}

              {viewingFeedback.comment && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Final Comment</Typography>
                  <Box
                    sx={{ ...richHtmlSx, maxHeight: '250px' }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewingFeedback.comment) }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenScoreDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyTrainings;
